import { Component } from '@theme/component';

/**
 * The appointment form (sections/hajjo-page-appointment.liquid).
 *
 * There is no booking system: as in the mockup's appointment.html, the form writes one
 * WhatsApp message, the way the store reads it, and opens it on wa.me for the shopper to
 * send. The lines of the message and the error wording come from the element's data
 * attributes, which the section fills from the locale.
 *
 * Without this script the form is a plain GET to the store's wa.me link, which opens the
 * chat without the prefilled text.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The appointment form.
 * @property {HTMLElement} error - The alert line under the button.
 *
 * @extends {Component<Refs>}
 */
class HajjoAppointment extends Component {
  requiredRefs = ['form', 'error'];

  /**
   * Checks the three fields the store needs, then opens the prefilled message.
   * Called from the form as `on:submit="/handleSubmit"`.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const { form, error } = this.refs;
    const data = this.dataset;
    error.textContent = '';

    const field = (/** @type {string} */ name) => {
      const element = form.elements.namedItem(name);
      return element instanceof HTMLInputElement || element instanceof HTMLSelectElement ? element : null;
    };

    const value = (/** @type {string} */ name) => String(field(name)?.value ?? '').trim();

    const name = value('name');
    const phone = value('phone');
    const date = value('date');

    /** @type {[boolean, string, string][]} */
    const checks = [
      [name.length < 2, data.errorName ?? '', 'name'],
      [phone.replace(/[^0-9]/g, '').length < 8, data.errorPhone ?? '', 'phone'],
      [!date, data.errorDate ?? '', 'date'],
    ];

    for (const [failed, message, fieldName] of checks) {
      if (!failed) continue;
      error.textContent = message;
      field(fieldName)?.focus();
      return;
    }

    const message = [
      data.msgTitle,
      `${data.msgStore} ${value('store')}`,
      `${data.msgReason} ${value('reason')}`,
      `${data.msgDate} ${date}`,
      `${data.msgSlot} ${value('slot')}`,
      `${data.msgName} ${name}`,
      `${data.msgPhone} ${phone}`,
    ].join('\n');

    // Encoded once with encodeURIComponent, as the mockup does: wa.me reads %20, not +.
    const url = new URL(form.action);
    url.search = '';
    window.open(`${url.toString()}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  }
}

if (!customElements.get('hajjo-appointment')) customElements.define('hajjo-appointment', HajjoAppointment);
